import { useEffect, useMemo, useState } from 'react';
import { Tabs, Tab, Box, Tooltip } from '@material-ui/core';
import HardwareIcon from '@mui/icons-material/Hardware';
import SpokeIcon from '@mui/icons-material/Spoke';
import WifiIcon from '@mui/icons-material/Wifi';
import TapAndPlayIcon from '@mui/icons-material/TapAndPlay';
import SignalCellular0BarIcon from '@mui/icons-material/SignalCellular0Bar';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  stateDetailCardSx,
  stateDetailLabelSx,
  stateDetailRowsSx,
  stateDetailRowSx,
  stateDetailValueSx,
  stateSectionHeaderSx,
  stateSectionIconSx,
  stateSectionTitleSx,
  stateTabSx,
  themePalette,
} from './stateTreeStyles';

const renderSectionTitle = (title, IconComponent) => (
  <Box sx={stateSectionHeaderSx}>
    {IconComponent ? <IconComponent style={stateSectionIconSx} /> : null}
    <div style={stateSectionTitleSx}>{title}</div>
  </Box>
);

const hasDisplayValue = (value) => {
  if (value === null || value === undefined) return false;
  return true;
};

const toYesNo = (value) => (value ? 'Si' : 'No');

const buildRow = ({ key, label, value, warningMessage = null, isWarning = false }) => {
  if (!hasDisplayValue(value)) return null;

  return {
    key,
    label,
    value,
    warningMessage,
    isWarning,
  };
};

const buildBooleanRow = ({ key, label, value }) => {
  if (typeof value !== 'boolean') return null;
  return buildRow({ key, label, value: toYesNo(value) });
};

const buildRows = (rows) => rows.filter(Boolean);

const getSignalWarning = (value, threshold, message) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue >= threshold) return {};

  return {
    warningMessage: message,
    isWarning: true,
  };
};

const MOBILE_SIGNAL_QUALITY_THRESHOLD = 30;

const getStatusContainer = (auto) => auto?.status || {};
const getWifiStatus = (auto) => getStatusContainer(auto)?.wifi_sta || auto?.wifi || null;
const getEthernetStatus = (auto) => getStatusContainer(auto)?.ethernet || auto?.ethernet || null;
const getMobileStatus = (auto) => getStatusContainer(auto)?.mobile || auto?.mobile || null;
const getDeviceInfo = (auto) => getStatusContainer(auto)?.device_info || auto?.device_info || null;
const getModbusServer = (auto) => getStatusContainer(auto)?.modbus_server || null;

function DetailRow({ label, value, warningMessage, isWarning = false }) {
  return (
    <Box
      sx={{
        ...stateDetailRowSx,
        gridTemplateColumns: warningMessage ? "minmax(160px, 220px) minmax(0, 1fr)" : stateDetailRowSx.gridTemplateColumns,
      }}
    >
      <div style={stateDetailLabelSx}>{label}</div>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: "0.45rem", minWidth: 0 }}>
        {warningMessage && (
          <Tooltip title={warningMessage}>
            <ReportProblemIcon style={{ color: themePalette.warning.main, fontSize: "1rem", marginTop: "0.08rem", flexShrink: 0 }} />
          </Tooltip>
        )}
        <div
          style={{
            ...stateDetailValueSx,
            color: isWarning ? themePalette.error.main : stateDetailValueSx.color,
          }}
        >
          {value}
        </div>
      </Box>
    </Box>
  );
}

function DetailSection({ title, icon: IconComponent, rows }) {
  if (!rows?.length) return null;

  return (
    <Box sx={stateDetailCardSx}>
      {renderSectionTitle(title, IconComponent)}
      <Box sx={stateDetailRowsSx}>
        {rows.map((row) => (
          <DetailRow
            key={row.key}
            label={row.label}
            value={row.value}
            warningMessage={row.warningMessage}
            isWarning={row.isWarning}
          />
        ))}
      </Box>
    </Box>
  );
}

const buildBaseRows = ({ auto }) => {
  const registration = auto?.registration;

  return buildRows([
    buildRow({ key: 'registration-model', label: 'Modelo', value: registration?.model || auto?.product || auto?.producto }),
    buildRow({ key: 'registration-firmware', label: 'Firmware', value: registration?.firmware || auto?.firmware }),
    buildRow({ key: 'registration-serial', label: 'Numero de serie', value: registration?.serial_number || auto?.product_code }),
    buildRow({ key: 'registration-email', label: 'Email', value: registration?.email }),
    buildRow({ key: 'registration-last-update', label: 'Ultima actualizacion', value: registration?.last_update }),
  ]);
};

const buildDeviceInfoRows = ({ auto }) => {
  const deviceInfo = getDeviceInfo(auto);
  if (!deviceInfo) return [];

  return buildRows([
    buildRow({ key: 'device-serial-number', label: 'Numero de serie', value: deviceInfo.serial_number }),
    buildRow({ key: 'device-model', label: 'Modelo', value: deviceInfo.device_model }),
    buildRow({ key: 'device-fw', label: 'Firmware', value: deviceInfo.device_fw }),
    buildRow({ key: 'hw-model', label: 'Modelo de hardware', value: deviceInfo.hw_model }),
    buildRow({ key: 'hw-fw', label: 'Firmware de hardware', value: deviceInfo.hw_fw }),
    buildRow({ key: 'power-on', label: 'Ultimo encendido', value: deviceInfo.power_on }),
    buildRow({ key: 'power-off', label: 'Ultimo apagado', value: deviceInfo.power_off }),
  ]);
};

const buildStateRows = ({ auto }) =>
  buildRows([
    buildBooleanRow({ key: 'online', label: 'Online', value: auto?.online }),
    buildRow({ key: 'last-record', label: 'Ultimo dato recibido', value: auto?.last_record }),
    buildRow({ key: 'last-status-update', label: 'Ultima actualizacion de estado', value: auto?.last_status_update }),
  ]);

const buildWifiRows = ({ auto, MAX_ACCEPTABLE_DBM_WIFI }) => {
  const wifi = getWifiStatus(auto);
  if (!wifi) return [];

  return buildRows([
    buildBooleanRow({ key: 'wifi-enabled', label: 'Habilitado', value: wifi.enabled }),
    buildBooleanRow({ key: 'wifi-connected', label: 'Conectado', value: wifi.connected }),
    buildRow({ key: 'wifi-quality', label: 'Calidad de senal (%)', value: wifi.quality }),
    buildRow({
      key: 'wifi-dbm',
      label: 'RSSI (dBm)',
      value: wifi.dbm,
      ...getSignalWarning(
        wifi.dbm,
        MAX_ACCEPTABLE_DBM_WIFI,
        `La intensidad esta por debajo del minimo (${MAX_ACCEPTABLE_DBM_WIFI} dBm)`
      ),
    }),
    buildRow({ key: 'wifi-ssid', label: 'SSID', value: wifi.ssid }),
    buildRow({ key: 'wifi-bssid', label: 'BSSID', value: wifi.bssid }),
    buildRow({ key: 'wifi-ip', label: 'IP', value: wifi.ip }),
    buildRow({ key: 'wifi-gateway', label: 'Puerta de enlace', value: wifi.gateway }),
    buildRow({ key: 'wifi-subnet-mask', label: 'Mascara de subred', value: wifi.subnet_mask }),
    buildRow({ key: 'wifi-dns-1', label: 'DNS1', value: wifi.dns_1 }),
    buildRow({ key: 'wifi-dns-2', label: 'DNS2', value: wifi.dns_2 }),
    buildRow({ key: 'wifi-mac', label: 'MAC', value: wifi.mac }),
  ]);
};

const buildMobileRows = ({ auto }) => {
  const mobile = getMobileStatus(auto);
  if (!mobile) return [];

  const mobileRssi = mobile.rssi ?? mobile.dbm;

  return buildRows([
    buildBooleanRow({ key: 'mobile-enabled', label: 'Habilitado', value: mobile.enabled }),
    buildBooleanRow({ key: 'mobile-connected', label: 'Conectado', value: mobile.connected }),
    buildRow({ key: 'mobile-ip', label: 'IP', value: mobile.ip }),
    buildRow({ key: 'mobile-dns1', label: 'DNS1', value: mobile.dns1 || mobile.dns_1 }),
    buildRow({ key: 'mobile-dns2', label: 'DNS2', value: mobile.dns2 || mobile.dns_2 }),
    buildRow({ key: 'mobile-mode', label: 'Modo', value: mobile.system_mode }),
    buildRow({ key: 'mobile-network', label: 'Red', value: mobile.network }),
    buildRow({
      key: 'mobile-quality',
      label: 'Calidad de senal (%)',
      value: mobile.quality,
      ...getSignalWarning(
        mobile.quality,
        MOBILE_SIGNAL_QUALITY_THRESHOLD,
        'Calidad de senal baja'
      ),
    }),
    buildRow({
      key: 'mobile-rssi',
      label: 'RSSI (dBm)',
      value: mobileRssi,
    }),
    buildRow({ key: 'mobile-rsrp', label: 'RSRP (dBm)', value: mobile.rsrp }),
    buildRow({ key: 'mobile-rsrq', label: 'RSRQ (dB)', value: mobile.rsrq }),
    buildRow({ key: 'mobile-sinr', label: 'SINR (dB)', value: mobile.sinr }),
    buildRow({ key: 'mobile-band', label: 'Banda', value: mobile.band }),
    buildRow({ key: 'mobile-imei', label: 'IMEI', value: mobile.imei }),
    buildRow({ key: 'mobile-sim', label: 'SIM', value: mobile.sim }),
    buildRow({ key: 'mobile-imsi', label: 'IMSI', value: mobile.imsi }),
    buildRow({ key: 'mobile-ccid', label: 'CCID', value: mobile.ccid }),
    buildRow({ key: 'mobile-apn', label: 'APN', value: mobile.apn }),
    buildRow({ key: 'mobile-apn-user', label: 'Usuario APN', value: mobile.apn_user }),
    buildRow({ key: 'mobile-apn-pass', label: 'Contrasena APN', value: mobile.apn_pass }),
  ]);
};

const buildEthernetRows = ({ auto }) => {
  const ethernet = getEthernetStatus(auto);
  if (!ethernet) return [];

  return buildRows([
    buildBooleanRow({ key: 'ethernet-enabled', label: 'Habilitado', value: ethernet.enabled }),
    buildBooleanRow({ key: 'ethernet-connected', label: 'Conectado', value: ethernet.connected }),
    buildRow({ key: 'ethernet-ip', label: 'IP', value: ethernet.ip }),
    buildRow({ key: 'ethernet-gateway', label: 'Puerta de enlace', value: ethernet.gateway }),
    buildRow({ key: 'ethernet-subnet-mask', label: 'Mascara de subred', value: ethernet.subnet_mask }),
    buildRow({ key: 'ethernet-dns-1', label: 'DNS1', value: ethernet.dns_1 }),
    buildRow({ key: 'ethernet-dns-2', label: 'DNS2', value: ethernet.dns_2 }),
    buildRow({ key: 'ethernet-mac', label: 'MAC', value: ethernet.mac }),
  ]);
};

const buildPowermateRows = ({ auto }) => {
  if (!auto?.powermate) return [];

  return buildRows([
    buildRow({ key: 'availability', label: 'Disponibilidad', value: auto?.powermate?.availability }),
    buildRow({
      key: 'integrity',
      label: 'Integridad de datos',
      value: auto?.powermate?.data_integrity,
    }),
  ]);
};

const buildModbusRows = ({ auto }) => {
  const modbusServer = getModbusServer(auto);
  if (!modbusServer) return [];

  return buildRows([
    buildBooleanRow({ key: 'modbus-tcp-enabled', label: 'Habilitado TCP', value: modbusServer.tcp_server_enabled }),
    buildRow({ key: 'modbus-tcp-port', label: 'Puerto TCP', value: modbusServer.tcp_server_port }),
    buildBooleanRow({ key: 'modbus-rtu-enabled', label: 'Habilitado RTU', value: modbusServer.rtu_server_enabled }),
    buildRow({ key: 'modbus-rtu-id', label: 'ID', value: modbusServer.rtu_server_id }),
    buildRow({ key: 'modbus-rtu-baudrate', label: 'Baudrate', value: modbusServer.rtu_server_baudrate }),
    buildRow({ key: 'modbus-rtu-parity', label: 'Paridad', value: modbusServer.rtu_server_parity }),
    buildRow({ key: 'modbus-map', label: 'Mapa', value: modbusServer.map }),
    buildBooleanRow({ key: 'modbus-active', label: 'Activo', value: modbusServer.active }),
  ]);
};

function StatusTabs({
  selectedNode,
  selectedStatus,
  MAX_ACCEPTABLE_DBM_WIFI,
}) {
  const [tabIndex, setTabIndex] = useState(0);

  const automatesArray = useMemo(
    () => (selectedStatus?.automates ? Object.values(selectedStatus.automates) : []),
    [selectedStatus?.automates]
  );

  const statusList = useMemo(() => [selectedStatus, ...automatesArray], [automatesArray, selectedStatus]);
  const hasAutomates = automatesArray.length > 0;

  useEffect(() => {
    setTabIndex(0);
  }, [selectedNode?.id, statusList.length]);

  const handleChange = (_, newValue) => setTabIndex(newValue);

  const renderContent = (auto, index) => {
    const hardwareRows = buildBaseRows({ auto });
    const deviceInfoRows = buildDeviceInfoRows({ auto });
    const stateRows = buildStateRows({ auto });
    const wifiRows = buildWifiRows({ auto, MAX_ACCEPTABLE_DBM_WIFI });
    const mobileRows = buildMobileRows({ auto });
    const ethernetRows = buildEthernetRows({ auto });
    const powermateRows = buildPowermateRows({ auto });
    const modbusRows = buildModbusRows({ auto });

    return (
      <div key={`${auto?.id || selectedNode?.id || "state"}-${index}`} hidden={tabIndex !== index}>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: "0.6rem", paddingTop: "0.25rem" }}>
          <DetailSection title="Hardware" icon={HardwareIcon} rows={hardwareRows} />
          <DetailSection title="Informacion del Dispositivo" icon={HardwareIcon} rows={deviceInfoRows} />
          <DetailSection title="Estado" icon={SpokeIcon} rows={stateRows} />
          <DetailSection title="Conectividad WiFi" icon={WifiIcon} rows={wifiRows} />
          <DetailSection title="Conectividad LTE" icon={TapAndPlayIcon} rows={mobileRows} />
          <DetailSection title="Conectividad Ethernet" icon={SignalCellular0BarIcon} rows={ethernetRows} />
          <DetailSection title="Powermate" rows={powermateRows} />
          <DetailSection title="Servidor Modbus" icon={SpokeIcon} rows={modbusRows} />
        </Box>
      </div>
    );
  };

  return (
    <>
      {hasAutomates && (
        <Box sx={{ borderBottom: `1px solid ${themePalette.border.main}`, marginBottom: "0.1rem" }}>
          <Tabs
            value={tabIndex}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            style={{ minHeight: "2.45rem" }}
            TabIndicatorProps={{
              style: {
                backgroundColor: themePalette.primary.main,
                height: 3,
                borderRadius: 999,
              },
            }}
          >
            {statusList.map((auto, index) => (
              <Tab
                key={`${auto?.id || auto?.mac || index}`}
                label={index === 0 ? 'Medidor' : auto?.nombre || `Automate ${index}`}
                style={stateTabSx}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {!hasAutomates && <div>{renderContent(selectedStatus, 0)}</div>}
      {hasAutomates && statusList.map((auto, index) => renderContent(auto, index))}
    </>
  );
}

export default StatusTabs;
